import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { reportsApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Download, Filter, Calendar, Users, TrendingUp, Clock, RotateCcw, Target, Loader2, Sparkles, Bot, AlertCircle, CheckCircle2, RefreshCw } from "lucide-react";
import CallPerformanceCard from "../components/CallPerformanceCard";

const DEPT_OPTIONS = ["All", "Sales", "CS"];
const COUNTRY_OPTIONS = ["All", "India", "US"];
const PIE_COLORS = ["#EA580C", "#2563EB", "#9333EA", "#16A34A", "#E11D48", "#D97706", "#0891B2", "#6366F1"];

// Iter 134 (Apr 30, 2026) — recharts inline-object memoization fix.
// Passing fresh object literals (`tick={{fontSize:10}}`) as props forces
// recharts to re-render on every parent update because shallow equality
// fails. Hoisting these to module scope means they're referentially
// stable across every render — no behavioral change, just fewer redraws.
const AXIS_TICK_SM = { fontSize: 10 };
const LEGEND_STYLE = { fontSize: 11 };
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 12 };

const ReportsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [department, setDepartment] = useState("All");
  const [country, setCountry] = useState("All");
  const [summary, setSummary] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [peakHours, setPeakHours] = useState({});
  const [repeatRate, setRepeatRate] = useState(null);
  const [issueCategories, setIssueCategories] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (department !== "All") params.department = department;
    if (country !== "All") params.country = country;

    // Use Promise.allSettled so a single endpoint error doesn't wipe the whole page.
    // The old Promise.all meant if /ticket-graph timed out, summary + peak-hours were
    // never rendered either.
    const [sumRes, graphRes, peakRes, issueRes] = await Promise.allSettled([
      reportsApi.getSummary(params),
      reportsApi.getTicketGraph(params),
      reportsApi.getPeakHours(params),
      reportsApi.getIssueCategories(params),
    ]);

    const failures = [];
    if (sumRes.status === "fulfilled") setSummary(sumRes.value.data); else { setSummary(null); failures.push("summary"); }
    if (graphRes.status === "fulfilled") setGraphData(graphRes.value.data.graph || []); else { setGraphData([]); failures.push("ticket graph"); }
    if (peakRes.status === "fulfilled") setPeakHours(peakRes.value.data.heatmap || {}); else { setPeakHours({}); failures.push("peak hours"); }
    if (issueRes.status === "fulfilled") setIssueCategories(issueRes.value.data.categories || []); else { setIssueCategories([]); failures.push("issues"); }

    if (user?.role === "admin" || user?.role === "leader" || user?.role === "super_admin") {
      try {
        const repRes = await reportsApi.getRepeatContacts(params);
        setRepeatRate(repRes.data);
      } catch { setRepeatRate(null); }
    }

    if (failures.length > 0) {
      // Log full error details to console so the user can share them with support
      [sumRes, graphRes, peakRes, issueRes].forEach((r, i) => {
        if (r.status === "rejected") {
          const names = ["summary", "ticket-graph", "peak-hours", "issue-categories"];
          console.error(`[Reports] /${names[i]} failed:`, r.reason?.response?.status, r.reason?.response?.data || r.reason?.message);
        }
      });
      toast.error(`Some reports failed to load: ${failures.join(", ")}. Check browser console for details.`);
    }
    setLoading(false);
  }, [startDate, endDate, department, country, user?.role]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Iter 152.22f (May 2026, Yash) — proper CSV with India / US split.
  // The previous export only emitted per-user rows; a finance/ops user
  // looking at it had to manually sum India-only vs US-only figures.
  // Now the bottom of the file has explicit country subtotals, a
  // department subtotal, and a grand total — and every cell is properly
  // CSV-escaped so commas in names/depts (or any future country list)
  // never spill across columns.
  const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const sumRows = (rows) => rows.reduce((acc, s) => ({
    total_tickets:    acc.total_tickets    + (s.total_tickets    || 0),
    open_tickets:     acc.open_tickets     + (s.open_tickets     || 0),
    pending_tickets:  acc.pending_tickets  + (s.pending_tickets  || 0),
    solved_tickets:   acc.solved_tickets   + (s.solved_tickets   ?? s.closed_tickets ?? 0),
    ai_solved_tickets:acc.ai_solved_tickets+ (s.ai_solved_tickets|| 0),
    expired_tickets:  acc.expired_tickets  + (s.expired_tickets  || 0),
    total_replies:    acc.total_replies    + (s.total_replies    || 0),
    ai_replies:       acc.ai_replies       + (s.ai_replies       || 0),
  }), {
    total_tickets: 0, open_tickets: 0, pending_tickets: 0, solved_tickets: 0,
    ai_solved_tickets: 0, expired_tickets: 0, total_replies: 0, ai_replies: 0,
  });

  const exportCSV = () => {
    if (!summary?.user_stats) return;
    // Iter 153 (Feb 2026, Yash) — clearer column names + proper credit
    // attribution. New columns: "Tickets Touched" (= chats agent
    // actually replied on), "Solved by Agent" (= chats agent replied
    // on AND now SOLVED), "Agent Replies", "AI Replies", split out so
    // the manager can see "Rahul replied on 5 chats and 3 closed."
    const rows = [[
      "Name", "Role", "Department", "Country",
      "Currently-Assigned Tickets",
      "Open (Assigned)", "Pending (Assigned)", "Expired (Assigned)",
      "Tickets Touched",
      "Solved by Agent",
      "Solved by AI (Assigned)",
      "AI-Handed-Off Replies",
      "Agent Replies",
      "AI Replies (on Assigned)",
      "Avg First Reply Time (min)",
      "Resolution Rate (Solved/Touched %)",
    ]];
    summary.user_stats.forEach(s => {
      rows.push([
        s.name, s.role, s.department, s.country,
        s.total_tickets,
        s.open_tickets ?? 0,
        s.pending_tickets ?? 0,
        s.expired_tickets ?? 0,
        s.tickets_touched ?? 0,
        s.tickets_solved_by_agent ?? s.solved_tickets ?? 0,
        s.ai_solved_tickets ?? 0,
        s.ai_handoff_replies ?? 0,
        s.total_replies ?? 0,
        s.ai_replies ?? 0,
        s.avg_frt_minutes,
        s.resolution_rate,
      ]);
    });

    // Subtotals — empty row, then one block per country, per dept, then grand.
    const pushSubtotal = (label, group) => {
      const t = sumRows(group);
      // Avg FRT across the group (replies-weighted mean)
      const totalRep = group.reduce((a, s) => a + (s.total_replies || 0), 0);
      const frtNum = totalRep > 0
        ? group.reduce((a, s) => a + ((s.avg_frt_minutes || 0) * (s.total_replies || 0)), 0) / totalRep
        : 0;
      // Resolution rate across the group: solved-by-agent / tickets-touched
      const groupTouched = group.reduce((a, s) => a + (s.tickets_touched || 0), 0);
      const groupSolved = group.reduce((a, s) => a + (s.tickets_solved_by_agent || s.solved_tickets || 0), 0);
      const resRate = groupTouched > 0
        ? Math.round((groupSolved / groupTouched) * 1000) / 10
        : 0;
      const groupAiHandoff = group.reduce((a, s) => a + (s.ai_handoff_replies || 0), 0);
      rows.push([
        label, "", "", "",
        t.total_tickets, t.open_tickets, t.pending_tickets, t.expired_tickets,
        groupTouched, groupSolved, t.ai_solved_tickets, groupAiHandoff,
        t.total_replies, t.ai_replies,
        Math.round(frtNum * 10) / 10,
        resRate,
      ]);
    };

    rows.push([]); // blank separator row

    // Country subtotals (India, US, anything else)
    const byCountry = {};
    summary.user_stats.forEach(s => {
      const k = s.country || "Unknown";
      (byCountry[k] = byCountry[k] || []).push(s);
    });
    Object.keys(byCountry).sort().forEach(c => pushSubtotal(`SUBTOTAL — ${c}`, byCountry[c]));

    // Department subtotals
    const byDept = {};
    summary.user_stats.forEach(s => {
      const k = s.department || "Unknown";
      (byDept[k] = byDept[k] || []).push(s);
    });
    rows.push([]);
    Object.keys(byDept).sort().forEach(d => pushSubtotal(`SUBTOTAL — ${d}`, byDept[d]));

    // Grand total
    rows.push([]);
    pushSubtotal("GRAND TOTAL", summary.user_stats);

    const csv = rows.map(r => r.map(csvEscape).join(",")).join("\n");
    // Prepend UTF-8 BOM so Excel opens "₹/₹" symbols and special chars correctly
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `report_${startDate}_${endDate}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported as CSV");
  };

  // Iter 141 — Totals now track the full WATI status set plus AI split so the
  // Reports header cards show the same breakdown managers see in the table.
  const totals = summary?.user_stats?.reduce((acc, s) => ({
    tickets: acc.tickets + (s.total_tickets || 0),
    open: acc.open + (s.open_tickets || 0),
    pending: acc.pending + (s.pending_tickets || 0),
    solved: acc.solved + (s.solved_tickets ?? s.closed_tickets ?? 0),
    ai_solved: acc.ai_solved + (s.ai_solved_tickets || 0),
    expired: acc.expired + (s.expired_tickets || 0),
    replies: acc.replies + (s.total_replies || 0),
    ai_replies: acc.ai_replies + (s.ai_replies || 0),
  }), { tickets: 0, open: 0, pending: 0, solved: 0, ai_solved: 0, expired: 0, replies: 0, ai_replies: 0 }) || {};

  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const HOURS = Array.from({ length: 24 }, (_, i) => i);

  const canSeeFullReports = user?.role === "admin" || user?.role === "leader" || user?.role === "super_admin";

  return (
    <div data-testid="reports-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[--text-primary] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Reports</h1>
          <p className="text-sm text-[--text-muted]">{canSeeFullReports ? "Full analytics & team performance" : "Your personal productivity"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white border-2 border-[--border] rounded-full px-3 py-1.5">
            <Calendar size={14} className="text-[--text-muted]" />
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="text-xs border-none outline-none bg-transparent" data-testid="report-start-date" />
            <span className="text-xs text-[--text-muted]">to</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="text-xs border-none outline-none bg-transparent" data-testid="report-end-date" />
          </div>
          {canSeeFullReports && (
            <>
              <select value={department} onChange={(e) => setDepartment(e.target.value)} className="text-xs border-2 border-[--border] rounded-full px-3 py-2 bg-white font-bold" data-testid="report-dept-filter">
                {DEPT_OPTIONS.map(d => <option key={d} value={d}>{d === "All" ? "All Depts" : d}</option>)}
              </select>
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="text-xs border-2 border-[--border] rounded-full px-3 py-2 bg-white font-bold" data-testid="report-country-filter">
                {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c === "All" ? "All Countries" : c}</option>)}
              </select>
            </>
          )}
          <button
            onClick={fetchAll}
            disabled={loading}
            className="btn-secondary text-xs py-2 flex items-center gap-1 disabled:opacity-60"
            data-testid="reports-refresh-btn"
            title="Re-fetch all report data (skips browser cache)"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
          <button onClick={exportCSV} className="btn-primary text-xs py-2 flex items-center gap-1" data-testid="export-csv-btn">
            <Download size={14} /> Export CSV
          </button>
          {user?.role === "super_admin" && (
            <Link
              to="/reports/ai-quality"
              className="btn-secondary text-xs py-2 flex items-center gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="ai-quality-link"
              title="Review a random sample of AI auto-replies"
            >
              <Sparkles size={14} /> AI Quality
            </Link>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 size={24} className="animate-spin text-[--accent]" /></div>
      ) : (
        <>
          {/* Summary Cards — Iter 141: WATI-aligned statuses + separate AI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="summary-cards">
            <StatCard icon={<Users size={20} />} label="Total Tickets" value={totals.tickets} color="orange" />
            <StatCard icon={<Clock size={20} />} label="Open" value={totals.open} color="blue" />
            <StatCard icon={<AlertCircle size={20} />} label="Pending" value={totals.pending} color="yellow" />
            <StatCard icon={<CheckCircle2 size={20} />} label="Solved (Agent)" value={totals.solved} color="green" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="summary-cards-ai">
            <StatCard icon={<Bot size={20} />} label="AI Solved" value={totals.ai_solved} color="purple" />
            <StatCard icon={<Bot size={20} />} label="AI Replies" value={totals.ai_replies} color="purple" />
            <StatCard icon={<TrendingUp size={20} />} label="Agent Replies" value={totals.replies} color="orange" />
            <StatCard icon={<Target size={20} />} label="Expired" value={totals.expired} color="red" />
          </div>

          {/* Department & Country Stats */}
          {canSeeFullReports && summary?.department_stats?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card p-5" data-testid="dept-stats">
                <h3 className="text-sm font-bold text-[--text-primary] mb-3" style={{ fontFamily: "Outfit" }}>Department Overview</h3>
                <div className="space-y-3">
                  {summary.department_stats.map(d => (
                    <div key={d.department} className="flex items-center justify-between p-3 bg-[--bg-cream] rounded-xl">
                      <div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${d.department === "CS" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>{d.department}</span>
                        <p className="text-xs text-[--text-muted] mt-1">
                          {d.total_tickets} tickets · <span className="text-green-700">{d.solved_tickets ?? d.closed_tickets ?? 0} solved</span> · <span className="text-purple-700">{d.ai_solved_tickets ?? 0} AI-solved</span>
                        </p>
                        <p className="text-[10px] text-[--text-muted] mt-0.5">
                          {d.total_replies ?? 0} agent replies · {d.ai_replies ?? 0} AI replies
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-[--text-primary]">FRT: {d.avg_frt_minutes}m</p>
                        <p className="text-xs text-[--text-muted]">Resolution: {d.resolution_rate}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card p-5" data-testid="country-stats">
                <h3 className="text-sm font-bold text-[--text-primary] mb-3" style={{ fontFamily: "Outfit" }}>Country Overview</h3>
                <div className="space-y-3">
                  {(summary.country_stats || []).map(c => (
                    <div key={c.country} className="flex items-center justify-between p-3 bg-[--bg-cream] rounded-xl">
                      <div>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{c.country}</span>
                        <p className="text-xs text-[--text-muted] mt-1">{c.total_tickets} tickets · {c.closed_tickets ?? 0} solved · {c.total_replies} replies</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-[--text-primary]">FRT: {c.avg_frt_minutes}m</p>
                        <p className="text-xs text-[--text-muted]">Resolution: {c.resolution_rate}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Ticket Graph */}
          {graphData.length > 0 && (
            <div className="card p-5" data-testid="ticket-graph">
              <h3 className="text-sm font-bold text-[--text-primary] mb-4" style={{ fontFamily: "Outfit" }}>Tickets Received vs Replied</h3>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={graphData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                    <XAxis dataKey="date" tick={AXIS_TICK_SM} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={AXIS_TICK_SM} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend wrapperStyle={LEGEND_STYLE} />
                    <Bar dataKey="received" fill="#EA580C" name="Received" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="replied" fill="#2563EB" name="Replied" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Agent Leaderboard — Iter 141: AI vs Human split columns */}
          {summary?.user_stats?.length > 0 && (
            <div className="card p-5" data-testid="agent-leaderboard">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-[--text-primary]" style={{ fontFamily: "Outfit" }}>Agent Leaderboard</h3>
                <span className="text-[10px] text-[--text-muted] flex items-center gap-1" title="Iter 153 — replies & solves are credited to the agent who actually replied (sender_id), not the agent currently assigned. AI columns reflect AI-handled work.">
                  <Bot size={12} className="text-purple-600" />
                  Credit by who replied · AI columns separate
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Agent</th>
                      {canSeeFullReports && <th>Dept</th>}
                      {canSeeFullReports && <th>Country</th>}
                      <th title="Tickets currently assigned to this agent">Assigned</th>
                      <th title="Open tickets currently assigned">Open</th>
                      <th title="Pending tickets currently assigned">Pending</th>
                      <th title="Distinct chats this agent has replied on (regardless of current assignment)">Touched</th>
                      <th title="Chats this agent replied on AND now wati_status=SOLVED" className="text-green-700">Solved</th>
                      <th title="AI-solved tickets currently assigned to this agent" className="text-purple-600">AI Solved</th>
                      <th title="Replies this agent made on chats that AI was actively handling (true follow-ups)" className="text-violet-700">AI Hand-off</th>
                      <th title="Total outgoing human messages by this agent">Replies</th>
                      <th title="AI replies on this agent's currently-assigned leads" className="text-purple-600">AI Replies</th>
                      <th>Avg FRT</th>
                      <th title="Solved-by-Agent / Tickets-Touched">Resolution %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.user_stats.map((s, i) => (
                      <tr key={s.user_id} data-testid={`leaderboard-row-${s.user_id}`}>
                        <td><span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-extrabold ${i < 3 ? "bg-[--accent] text-white" : "bg-gray-100 text-gray-500"}`}>{i + 1}</span></td>
                        <td>
                          <p className="font-bold text-[--text-primary] text-sm">{s.name}</p>
                          <p className="text-[10px] text-[--text-muted]">{s.role}</p>
                        </td>
                        {canSeeFullReports && <td><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.department === "CS" ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700"}`}>{s.department}</span></td>}
                        {canSeeFullReports && <td className="text-xs font-medium">{s.country}</td>}
                        <td className="font-bold">{s.total_tickets}</td>
                        <td className="text-blue-600 font-medium">{s.open_tickets ?? 0}</td>
                        <td className="text-yellow-700 font-medium">{s.pending_tickets ?? 0}</td>
                        <td className="font-bold text-slate-700" data-testid={`tickets-touched-${s.user_id}`}>{s.tickets_touched ?? 0}</td>
                        <td className="text-green-600 font-medium" data-testid={`solved-by-agent-${s.user_id}`}>{s.tickets_solved_by_agent ?? s.solved_tickets ?? 0}</td>
                        <td className="text-purple-600 font-medium" data-testid={`ai-solved-${s.user_id}`}>{s.ai_solved_tickets ?? 0}</td>
                        <td className="text-violet-700 font-medium" data-testid={`ai-handoff-${s.user_id}`}>{s.ai_handoff_replies ?? 0}</td>
                        <td className="font-bold text-[--accent]">{s.total_replies}</td>
                        <td className="text-purple-600 font-medium" data-testid={`ai-replies-${s.user_id}`}>{s.ai_replies ?? 0}</td>
                        <td><span className={`text-xs font-bold ${s.avg_frt_minutes < 15 ? "text-green-600" : s.avg_frt_minutes < 60 ? "text-yellow-600" : "text-red-600"}`}>{s.avg_frt_minutes}m</span></td>
                        <td><span className={`text-xs font-bold ${s.resolution_rate > 70 ? "text-green-600" : s.resolution_rate > 40 ? "text-yellow-600" : "text-red-600"}`}>{s.resolution_rate}%</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Bottom row: Issue Categories + Peak Hours + Repeat Rate */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Issue Category Breakdown */}
            {issueCategories.length > 0 && (
              <div className="card p-5" data-testid="issue-categories">
                <h3 className="text-sm font-bold text-[--text-primary] mb-4" style={{ fontFamily: "Outfit" }}>Issue Categories</h3>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={issueCategories} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={80} label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {issueCategories.map((c, i) => <Cell key={c.category || `cat-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Peak Hours Heatmap */}
            {canSeeFullReports && Object.keys(peakHours).length > 0 && (
              <div className="card p-5" data-testid="peak-hours">
                <h3 className="text-sm font-bold text-[--text-primary] mb-3" style={{ fontFamily: "Outfit" }}>Peak Hours</h3>
                <div className="overflow-x-auto">
                  <div className="grid gap-0.5" style={{ gridTemplateColumns: `40px repeat(24, 1fr)` }}>
                    <div />
                    {HOURS.map(h => <div key={h} className="text-[8px] text-center text-[--text-muted] font-bold">{h}</div>)}
                    {DAYS.map((day, di) => (
                      <>
                        <div key={`label-${di}`} className="text-[9px] font-bold text-[--text-muted] flex items-center">{day}</div>
                        {HOURS.map(h => {
                          const val = peakHours[`${di}-${h}`] || 0;
                          const maxVal = Math.max(...Object.values(peakHours), 1);
                          const intensity = val / maxVal;
                          return <div key={`${di}-${h}`} className="w-full aspect-square rounded-sm" title={`${day} ${h}:00 — ${val} messages`}
                            style={{ background: val > 0 ? `rgba(234, 88, 12, ${0.1 + intensity * 0.9})` : "#F5F5F4" }} />;
                        })}
                      </>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Repeat Contact Rate */}
            {canSeeFullReports && repeatRate && (
              <div className="card p-5" data-testid="repeat-contacts">
                <h3 className="text-sm font-bold text-[--text-primary] mb-4" style={{ fontFamily: "Outfit" }}>Repeat Contacts</h3>
                <div className="flex flex-col items-center justify-center h-[180px]">
                  <div className="text-4xl font-extrabold text-[--accent]" style={{ fontFamily: "Outfit" }}>{repeatRate.rate}%</div>
                  <p className="text-xs text-[--text-muted] mt-2">Customers contacting again within 7 days</p>
                  <p className="text-xs text-[--text-muted] mt-1">{repeatRate.repeat_contacts} of {repeatRate.total_leads} leads</p>
                  <div className="mt-3 flex items-center gap-1">
                    <RotateCcw size={12} className="text-[--text-muted]" />
                    <span className="text-[10px] text-[--text-muted]">Lower is better — means issues resolved first time</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Telecmi call performance per agent — last 30 days */}
          {canSeeFullReports && <CallPerformanceCard />}
        </>
      )}
    </div>
  );
};

const StatCard = ({ icon, label, value, color }) => (
  <div className="stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className={`stat-icon ${color}`}>{icon}</div>
    <div className="stat-value mt-2">{(value ?? 0).toLocaleString()}</div>
    <div className="stat-label">{label}</div>
  </div>
);

export default ReportsPage;
